import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} HeaderDrawerRefs
 * @property {HTMLDetailsElement} details - The original details element that owns the menu trigger.
 * @property {HTMLDivElement} menuDrawer - The menu drawer content.
 */

/**
 * Manages the header navigation drawer.
 *
 * The main drawer intentionally mirrors the cart drawer architecture: it is moved into a native
 * modal <dialog>, which places it in the browser top layer and prevents the floating/glass header
 * from becoming its positioning or stacking context. The menu uses the theme's mirrored drawer
 * animation so it enters from the left while the cart enters from the right.
 *
 * Nested menu levels continue to use their existing <details> elements.
 *
 * @extends {Component<HeaderDrawerRefs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {HTMLElement | null} */
  #mainTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    // The legacy menu used the top-level <details scroll-lock> state to lock the page. The main
    // menu is now a native modal dialog, so allowing that old details behavior to run creates two
    // competing open/scroll-lock systems. Keep <details> only as the existing trigger wrapper and
    // prevent it from changing state natively.
    this.refs.details.removeAttribute('scroll-lock');
    this.refs.details.removeAttribute('open');

    this.#mainTrigger = this.refs.details.querySelector(':scope > summary');
    this.#mainTrigger?.addEventListener('click', this.#preventNativeDetailsToggle, { capture: true });

    this.#setupMainDialog();
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#mainTrigger?.removeEventListener('click', this.#preventNativeDetailsToggle, { capture: true });
    this.#mainTrigger = null;

    if (this.#dialog?.open) {
      this.#dialog.close();
      this.#restorePageScroll();
    }

    this.#dialog?.removeEventListener('click', this.#handleDialogClick);
    this.#dialog?.removeEventListener('cancel', this.#handleDialogCancel);
  }

  /**
   * Whether the main menu dialog is currently open.
   */
  get isOpen() {
    return this.#dialog?.open ?? false;
  }

  /**
   * Prevent the summary element from toggling its parent <details>. Event propagation is left
   * intact so the theme component event handler can still call /toggle normally.
   *
   * @param {MouseEvent} event
   */
  #preventNativeDetailsToggle = (event) => {
    event.preventDefault();
  };

  /**
   * Build the same native-dialog structure used by the cart drawer and move the existing menu
   * content into it. Keeping the dialog inside <header-drawer> preserves all existing component
   * event routing for submenu/back/close controls.
   */
  #setupMainDialog() {
    const existingDialog = this.querySelector(':scope > dialog[data-header-menu-dialog]');

    if (existingDialog instanceof HTMLDialogElement) {
      this.#dialog = existingDialog;
    } else {
      const dialog = document.createElement('dialog');
      dialog.dataset.headerMenuDialog = '';
      dialog.className = 'header-menu-dialog dialog-modal dialog-drawer dialog-drawer--right';

      // Match the cart drawer geometry, mirrored to the left side.
      dialog.style.position = 'fixed';
      dialog.style.inset = '0 auto 0 0';
      dialog.style.overflow = 'hidden';
      dialog.style.borderRadius = '0';
      dialog.style.width = 'var(--sidebar-width)';
      dialog.style.maxWidth = '95vw';
      dialog.style.height = '100%';
      dialog.style.maxHeight = '100dvh';
      dialog.style.margin = '0 auto 0 0';
      dialog.style.padding = '0';
      dialog.style.border = '0';
      dialog.style.borderRight = 'var(--style-border-drawer)';
      dialog.style.boxShadow = 'var(--shadow-drawer)';
      dialog.style.backgroundColor = 'var(--color-background)';

      const trigger = this.refs.details.querySelector(':scope > summary');
      const label = trigger?.getAttribute('aria-label');
      if (label) dialog.setAttribute('aria-label', label);

      // Copy the menu color-scheme class onto the dialog so it behaves like the cart dialog.
      for (const className of this.refs.menuDrawer.classList) {
        if (className.startsWith('color-')) dialog.classList.add(className);
      }

      this.append(dialog);
      dialog.append(this.refs.menuDrawer);
      this.#dialog = dialog;
    }

    this.#prepareDrawerContent();
    this.#dialog.addEventListener('click', this.#handleDialogClick);
    this.#dialog.addEventListener('cancel', this.#handleDialogCancel);
  }

  /**
   * Neutralize the legacy fixed-position drawer shell now that the same content is hosted inside
   * the native dialog. Internal menu/submenu styles remain untouched.
   */
  #prepareDrawerContent() {
    const drawer = this.refs.menuDrawer;

    drawer.style.position = 'relative';
    drawer.style.inset = 'auto';
    drawer.style.transform = 'none';
    drawer.style.visibility = 'visible';
    drawer.style.opacity = '1';
    drawer.style.display = 'flex';
    drawer.style.width = '100%';
    drawer.style.maxWidth = 'none';
    drawer.style.height = '100%';
    drawer.style.maxHeight = '100%';
    drawer.style.border = '0';
    drawer.style.boxShadow = 'none';
  }

  /**
   * Toggle the main drawer. The trigger remains visually unchanged, matching the cart trigger;
   * the drawer's own close button closes the modal.
   *
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();
    return this.isOpen ? this.close() : this.#openMainDialog();
  }

  /**
   * Open either the main drawer or an existing nested submenu.
   *
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);

    if (!target || details === this.refs.details) {
      event?.preventDefault();
      return this.#openMainDialog();
    }

    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');

      const submenu = details.querySelector('.menu-drawer__submenu');
      onAnimationEnd(submenu || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Open the main menu using the same native modal behavior as the cart drawer.
   *
   * The dialog is opened before the page is locked. This is deliberate: if Chrome rejects
   * showModal() because Shopify has just morphed/replaced the header DOM, the page must remain
   * interactive rather than being stranded with body position: fixed and no visible drawer.
   */
  #openMainDialog() {
    let dialog = this.#dialog;

    // Theme-editor/section morphs can invalidate a previously created dialog reference. Rebuild it
    // from the current component DOM before attempting to open.
    if (!(dialog instanceof HTMLDialogElement) || !dialog.isConnected) {
      this.#dialog = null;
      this.#setupMainDialog();
      dialog = this.#dialog;
    }

    if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;

    const trigger = this.refs.details.querySelector(':scope > summary');
    const scrollY = window.scrollY;

    this.refs.details.removeAttribute('open');
    trigger?.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(dialog);

    try {
      dialog.showModal();
    } catch (error) {
      trigger?.setAttribute('aria-expanded', 'false');
      console.error('Failed to open the mobile menu dialog:', error);
      return;
    }

    this.#previousScrollY = scrollY;

    // Only lock the document after showModal() has succeeded.
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  /**
   * Close the main menu dialog with the same animated close sequence used by theme dialogs.
   */
  async close() {
    const dialog = this.#dialog;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    dialog.style.animation = 'none';
    // Force the browser to commit the animation reset before the closing class is applied.
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#restorePageScroll();

    const trigger = this.refs.details.querySelector(':scope > summary');
    trigger?.setAttribute('aria-expanded', 'false');
    this.refs.details.removeAttribute('open');

    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    removeTrapFocus();

    const openDetails = this.refs.menuDrawer.querySelectorAll('details[open]:not(accordion-custom > details)');
    openDetails.forEach(reset);
  }

  /**
   * Go back one nested submenu level.
   *
   * @param {Event} [event]
   */
  back(event) {
    const details = this.#getDetailsElement(event);
    if (details === this.refs.details) return;

    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const submenu = details.querySelector('.menu-drawer__submenu');
    onAnimationEnd(
      submenu || details,
      () => {
        reset(details);

        const remainingOpenSubmenu = this.refs.menuDrawer.querySelector(
          'details.menu-open, details[open]:not(accordion-custom > details)'
        );

        if (!remainingOpenSubmenu) {
          this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
        }

        if (this.#dialog?.open) {
          trapFocus(this.#dialog);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Get the closest details element to an event target.
   *
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Clicking the native dialog backdrop should close it, matching the cart drawer.
   *
   * @param {MouseEvent} event
   */
  #handleDialogClick = (event) => {
    if (event.target === this.#dialog) this.close();
  };

  /**
   * Route Escape through our animated close sequence instead of allowing the browser to close the
   * dialog immediately.
   *
   * @param {Event} event
   */
  #handleDialogCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * Restore the document after modal scroll locking.
   */
  #restorePageScroll() {
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Attach animation-end cleanup to the menu's existing animated elements.
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   *
   * @param {Element} container
   */
  preventInitialAccordionAnimations(container) {
    const content = container.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state.
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
